import { Features } from '@/components/organisms/Features'
import { FinalCTA } from '@/components/organisms/FinalCTA'
import { Footer } from '@/components/organisms/Footer'
import { Hero } from '@/components/organisms/Hero'
import { HowItWorks } from '@/components/organisms/HowItWorks'
import { PainPoints } from '@/components/organisms/PainPoints'
import { Pricing } from '@/components/organisms/Pricing'
import { TrustStrip } from '@/components/organisms/TrustStrip'
import { WhyNotChatGPT } from '@/components/organisms/WhyNotChatGPT'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'

// ─── Shared ───────────────────────────────────────────────────────────────────

const cx = 'max-w-[1100px] mx-auto px-5 sm:px-8'

function BtnPrimary({ to, children, className = '' }) {
  return (
    <Link
      to={to}
      className={`inline-block bg-ds-text text-ds-bg font-display font-bold text-[13px] tracking-btn uppercase px-[22px] py-[11px] rounded-sharp no-underline hover:opacity-85 transition-opacity duration-150 ${className}`}
    >
      {children}
    </Link>
  )
}

// ─── Navbar ───────────────────────────────────────────────────────────────────

function Navbar() {
  const [open, setOpen] = useState(false)

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 border-b border-ds-border h-14"
      style={{ background: 'rgba(13,13,13,0.96)' }}
    >
      <div className={`${cx} h-full flex items-center justify-between`}>
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 no-underline">
          <div className="w-7 h-7 bg-ds-red rounded-sharp flex items-center justify-center flex-shrink-0">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M1.5 3.2L6.5 1L11.5 3.2V6.5C11.5 9.5 6.5 12 6.5 12C6.5 12 1.5 9.5 1.5 6.5V3.2Z"
                fill="white"
                opacity="0.9"
              />
            </svg>
          </div>
          <span className="font-display font-black text-[15px] tracking-[0.04em] uppercase text-ds-text">
            RDK<span className="text-ds-red">·</span>CC
          </span>
        </Link>

        {/* Nav links — desktop */}
        <ul className="hidden md:flex items-center gap-8 list-none m-0 p-0">
          {[
            ['#funcionalidades', 'Funcionalidades'],
            ['#como-funciona', 'Cómo funciona'],
            ['#precios', 'Precios'],
          ].map(([href, label]) => (
            <li key={href}>
              <a
                href={href}
                className="font-display font-bold text-[12px] tracking-[0.10em] uppercase text-ds-muted no-underline hover:text-ds-text transition-colors duration-150"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>

        {/* CTA — desktop */}
        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/auth"
            className="font-display font-bold text-[12px] tracking-btn uppercase text-ds-muted no-underline hover:text-ds-text transition-colors duration-150"
          >
            Iniciar sesión
          </Link>
          <BtnPrimary to="/auth">Empezar gratis</BtnPrimary>
        </div>

        {/* Hamburger — mobile */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden flex flex-col justify-center gap-[5px] w-8 h-8 bg-transparent border-0 cursor-pointer p-1"
          aria-label="Menú"
        >
          <span
            className={`block w-5 h-px bg-ds-text transition-all duration-200 origin-center ${open ? 'rotate-45 translate-y-[6px]' : ''}`}
          />
          <span
            className={`block w-5 h-px bg-ds-text transition-all duration-200 ${open ? 'opacity-0' : ''}`}
          />
          <span
            className={`block w-5 h-px bg-ds-text transition-all duration-200 origin-center ${open ? '-rotate-45 -translate-y-[6px]' : ''}`}
          />
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden border-t border-ds-border"
          style={{ background: 'rgba(13,13,13,0.97)' }}
        >
          <div className={`${cx} py-6 flex flex-col gap-4`}>
            {[
              ['#funcionalidades', 'Funcionalidades'],
              ['#como-funciona', 'Cómo funciona'],
              ['#precios', 'Precios'],
            ].map(([href, label]) => (
              <a
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                className="font-display font-bold text-sm tracking-[0.10em] uppercase text-ds-muted no-underline hover:text-ds-text transition-colors"
              >
                {label}
              </a>
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t border-ds-border">
              <Link
                to="/auth"
                className="font-display font-bold text-sm tracking-btn uppercase text-ds-muted no-underline"
              >
                Iniciar sesión
              </Link>
              <BtnPrimary to="/auth" className="text-center">
                Empezar gratis
              </BtnPrimary>
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

// ─── Strip ────────────────────────────────────────────────────────────────────

const STRIP_ITEMS = [
  'Suena a ti, no a ChatGPT',
  'De 3 horas a 30 minutos',
  'Pipeline de 6 etapas',
  'B-Roll cinematográfico incluido',
  'Conecta tu canal YouTube',
]

function Strip() {
  return (
    <div className="hidden lg:block border-b border-ds-border py-5">
      <div className={`${cx} flex flex-col lg:flex-row lg:justify-center`}>
        {STRIP_ITEMS.map((item, i) => (
          <span
            key={i}
            className={`font-display font-bold text-[12px] tracking-caption uppercase text-ds-muted px-7 py-1.5 whitespace-nowrap text-center
              ${
                i < STRIP_ITEMS.length - 1
                  ? 'border-b border-ds-border lg:border-b-0 lg:border-r'
                  : ''
              }`}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-ds-bg">
      <Navbar />
      <Hero />
      <Strip />
      <PainPoints />
      <Features />
      <WhyNotChatGPT />
      <HowItWorks />
      <TrustStrip />
      <Pricing />
      <FinalCTA />
      <Footer />
    </div>
  )
}
